/**
 * Hook TanStack para criar Pré-Nota.
 * Responsável por executar a mutation de inclusão, tratar respostas e exibir feedback ao usuário.
 *
 * @returns Mutation TanStack para inclusão de pré-nota
 */

"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { post, apiClient } from "api";
import { parse } from "visuais";
import toast from "react-hot-toast";
import {
  PostPrenotaPayload,
  PostPreNotaResponse,
  PostPreNotaHookError,
  usePrenotaStore,
} from "@PN/inclusao";
import {
  formatProtheusError,
  formatProtheusSuccess,
  formatLoadingToast,
} from "./toast";
import { PRENOTAS_QUERY_KEY } from "@PN/tabela";

/**
 * Hook TanStack para criar Pré-Nota
 * Contém apenas a lógica de mutação de save
 *
 * @example
 * const { mutate, isPending, error } = usePostPreNota();
 */

type MutationContext = {
  loadingToast: string;
};

type PrenotaListItem = {
  REC?: number | string;
  F1_DOC?: string;
  F1_SERIE?: string;
  F1_FILIAL?: string;
  F1_FORNECE?: string;
  F1_LOJA?: string;
};

type ReconciliationLookupResponse = {
  success?: boolean;
  data?: PrenotaListItem | null;
};

type ReconciliationParams = {
  filial: string;
  numero: string;
  serie: string;
  fornecedor: string;
  loja: string;
  origem?: string;
};

const RECONCILIATION_TOTAL_MS = 30_000;
const RECONCILIATION_INTERVAL_MS = 3_000;
const RECONCILIATION_INITIAL_DELAY_MS = 15_000;
const DUPLICATE_KEY_MESSAGE =
  "Esta chave já foi dada entrada anteriormente no sistema";

function normalizeValue(value: unknown): string {
  return String(value || "").trim().toUpperCase();
}

function normalizeComparableCode(value: unknown): string {
  const normalized = normalizeValue(value);
  const stripped = normalized.replace(/^0+/, "");
  return stripped || "0";
}

function isInclusionRecoverableError(error: unknown): boolean {
  const e = error as any;
  const status = e?.response?.status;
  const code = String(e?.code || "").toUpperCase();
  const message = String(
    e?.message || e?.errorMessage || e?.response?.data?.errorMessage || ""
  ).toLowerCase();

  if (status === 500 || status === 502 || status === 503 || status === 504) return true;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT" || code === "ERR_NETWORK")
    return true;
  return message.includes("timeout") || message.includes("timed out");
}

function isRateioTotalValidationMessage(message: string | undefined): boolean {
  const normalized = String(message || "").toLowerCase();
  if (!normalized) return false;

  const hasRateioTerm =
    normalized.includes("rateio") || normalized.includes("rateado");
  const hasHundredPercentHint =
    normalized.includes("100%") ||
    normalized.includes("100 %") ||
    normalized.includes("100,00") ||
    normalized.includes("cem por cento");
  const hasSumHint = normalized.includes("soma") || normalized.includes("total");

  return hasRateioTerm && hasHundredPercentHint && hasSumHint;
}

function extractApiErrorMessage(payload: unknown): string | undefined {
  if (typeof payload === "string" && payload.trim()) {
    const text = payload.trim();
    try {
      const parsed = JSON.parse(text);
      return extractApiErrorMessage(parsed);
    } catch {
      const match = text.match(/"errorMessage"\s*:\s*"([^"]+)"/i);
      if (match?.[1]) return match[1].trim();
      return undefined;
    }
  }

  if (!payload || typeof payload !== "object") return undefined;
  const data = payload as Record<string, unknown>;

  const directMessage =
    data.errorMessage ??
    data.ErrorMessage ??
    data.message ??
    data.Message;

  if (typeof directMessage === "string" && directMessage.trim()) {
    return directMessage.trim();
  }

  if (data.Sucesso === false || data.Prenota === false) {
    const fallback = data.Mensagem;
    if (typeof fallback === "string" && fallback.trim()) {
      return fallback.trim();
    }
  }

  return undefined;
}

async function validateDuplicateKeyBeforeSave(chaveNFe?: string): Promise<void> {
  const chave = String(chaveNFe || "").trim();
  if (!chave) return;

  const response = await fetch("/api/prenotas/validaXML", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-validar-inclusao": "true",
    },
    body: JSON.stringify({
      chaveNFe: chave,
      buscarDados: false,
    }),
  });

  if (!response.ok) {
    const error = new Error(
      "Não foi possível validar a chave da NFe antes de salvar."
    ) as PostPreNotaHookError;
    error.cause = "validation";
    throw error;
  }

  const result = await response.json();
  if (result?.jaExiste) {
    const error = new Error(DUPLICATE_KEY_MESSAGE) as PostPreNotaHookError;
    error.cause = "validation";
    throw error;
  }
}

function matchReconciliationCandidate(
  item: PrenotaListItem,
  params: ReconciliationParams
): boolean {
  return (
    normalizeComparableCode(item.F1_DOC) === normalizeComparableCode(params.numero) &&
    normalizeComparableCode(item.F1_SERIE) === normalizeComparableCode(params.serie) &&
    normalizeComparableCode(item.F1_FILIAL) === normalizeComparableCode(params.filial) &&
    normalizeComparableCode(item.F1_FORNECE) ===
      normalizeComparableCode(params.fornecedor) &&
    normalizeComparableCode(item.F1_LOJA) === normalizeComparableCode(params.loja)
  );
}

async function lookupPrenotaAfterTimeout(
  params: ReconciliationParams
): Promise<string | null> {
  const xori = String(params.origem || "RodoApp2.0").trim() || "RodoApp2.0";
  const response = await fetch("/api/prenotas/reconciliacao", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filial: params.filial,
      numero: params.numero,
      serie: params.serie,
      fornecedor: params.fornecedor,
      loja: params.loja,
      xori,
    }),
  });

  if (!response.ok) return null;

  const json = (await response.json()) as ReconciliationLookupResponse;
  const candidate = json?.data;
  if (!candidate) return null;

  if (!matchReconciliationCandidate(candidate, params)) {
    throw new Error("Reconciliação inválida: dados retornados não conferem com a inclusão.");
  }

  if (candidate.REC == null) return null;

  const rec = String(candidate.REC).trim();
  return rec || null;
}

async function reconcilePrenotaAfterTimeout(
  params: ReconciliationParams
): Promise<string | null> {
  const toastId = toast.loading(
    "Timeout na inclusão. Aguardando 30s para validar se a pré-nota foi lançada.",
    { duration: Infinity }
  );

  try {
    // Evita falso negativo imediato: aguarda o backend finalizar persistência após timeout.
    const initialDelaySeconds = Math.ceil(RECONCILIATION_INITIAL_DELAY_MS / 1000);
    toast.loading(
      `Aguardando ${initialDelaySeconds}s para validar se a pré-nota foi lançada...`,
      { id: toastId, duration: Infinity }
    );
    await new Promise((resolve) => setTimeout(resolve, RECONCILIATION_INITIAL_DELAY_MS));
    const startedAt = Date.now();

    while (Date.now() - startedAt < RECONCILIATION_TOTAL_MS) {
      const elapsed = Date.now() - startedAt;
      const remainingSeconds = Math.max(
        0,
        Math.ceil((RECONCILIATION_TOTAL_MS - elapsed) / 1000)
      );

      toast.loading(
        `Validando lançamento da pré-nota no servidor... (${remainingSeconds}s)`,
        { id: toastId, duration: Infinity }
      );

      try {
        const rec = await lookupPrenotaAfterTimeout(params);
        if (rec) return rec;
      } catch (err) {
        console.warn("[RECONCILIACAO PRENOTA] tentativa falhou", {
          message: err instanceof Error ? err.message : String(err),
        });
      }

      await new Promise((resolve) => setTimeout(resolve, RECONCILIATION_INTERVAL_MS));
    }
  } finally {
    toast.dismiss(toastId);
  }

  return null;
}

function isVencimentoValido(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;

  const [ano, mes, dia] = v.split("-").map(Number);
  const data = new Date(ano, mes - 1, dia);

  return (
    data.getFullYear() === ano &&
    data.getMonth() === mes - 1 &&
    data.getDate() === dia
  );
}

export function usePostPreNota() {
  const queryClient = useQueryClient();
  const draft = usePrenotaStore((s) => s.draft);
  const hasHydrated = usePrenotaStore((s) => s.hasHydrated);
  const reset = usePrenotaStore((s) => s.actions.reset);

  // Função de reset que sempre tenta executar
  const safeReset = () => {
    try {
      if (reset) {
        reset();
        console.log("✅ [usePostPreNota] Store resetada com sucesso");
      } else {
        console.warn("⚠️ [usePostPreNota] Função reset não disponível");
      }
    } catch (error) {
      console.error("❌ [usePostPreNota] Erro ao resetar store:", error);
    }
  };

  return useMutation<
    PostPreNotaResponse,
    PostPreNotaHookError,
    void,
    MutationContext
  >({
    mutationFn: async () => {
      await validateDuplicateKeyBeforeSave(draft.documento.chaveNFe);

      // Validate required fields
      const errors: string[] = [];

      if (!draft.documento.fornecedor) errors.push("Fornecedor é obrigatório");
      if (!draft.documento.numero)
        errors.push("Número do documento é obrigatório");
      if (!draft.documento.serie) errors.push("Série é obrigatória");
      if (!draft.documento.dataEmissao)
        errors.push("Data de emissão é obrigatória");
      if (draft.produtos.length === 0)
        errors.push("Pelo menos um produto é obrigatório");

      // Última barreira antes do POST: garante que nenhuma parcela vá com
      // vencimento vazio, mesmo que tenha passado pelo zod antes de um
      // possível replace assíncrono das parcelas na store.
      const parcelasSemVencimento = (draft.pagamentos?.parcelas || [])
        .map((p, idx) => ({ ...p, idx }))
        .filter((p) => !p.vencimento || !p.vencimento.trim() || !isVencimentoValido(p.vencimento.trim()));

      if (parcelasSemVencimento.length > 0) {
        errors.push(
          `Vencimento não preenchido na(s) parcela(s): ${parcelasSemVencimento
            .map((p) => p.item || String(p.idx + 1))
            .join(", ")}`
        );
      }

      if (errors.length > 0) {
        const error = new Error(
          "Preencha todos os campos obrigatórios antes de salvar.\n" +
            errors.join("\n")
        ) as PostPreNotaHookError;
        error.cause = "validation";
        throw error;
      }

      // Build payload - adaptar para a estrutura atual
      const payload: PostPrenotaPayload = {
        filial: draft.filial,
        usuario: draft.usuario,
        origem: draft.origem,
        documento: draft.documento,
        opcoes: draft.opcoes,
        anexos: [],
        pagamentos: draft.pagamentos,
        rateios: draft.rateios,
        produtos: draft.produtos,
      };

      // Log do payload para debug
      console.log("🔍 [usePostPreNota] Payload sendo enviado:", {
        especie: payload.documento.especie,
        produtosCount: payload.produtos.length,
        rateiosCount: payload.rateios.length,
        tipoNota: payload.opcoes.tipoNota,
        payload: JSON.stringify(payload, null, 2),
      });
      
      // Create Pré-Nota (nova API via client)
      let resp: PostPreNotaResponse | undefined = undefined;
      try {
        const response = await post(
          apiClient.production,
          "/DocumentoDeEntrada/InclusaoPreNota20",
          JSON.stringify(payload),
          {
            headers: {
              "Content-Type": "text/plain",
            },
            responseType: "text",
          }
        );
        const resultText =
          typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data);

        console.log("🔍 [usePostPreNota] Resposta da API:", {
          status: response.status,
          data: resultText,
          headers: response.headers,
        });

        try {
          resp = JSON.parse(resultText);
        } catch {
          console.log(
            "❌ [usePostPreNota] Resposta da API não é um JSON válido: " +
              resultText
          );

          const rawMessage = extractApiErrorMessage(resultText);
          if (rawMessage) {
            const customError = new Error(rawMessage) as PostPreNotaHookError;
            customError.cause = "api_logic";
            (customError as any).errorMessage = rawMessage;
            throw customError;
          }
        }

        const apiMessage = extractApiErrorMessage(resp);
        if (apiMessage) {
          const customError = new Error(apiMessage) as PostPreNotaHookError;
          customError.cause = "api_logic";
          (customError as any).errorMessage = apiMessage;
          throw customError;
        }
      } catch (error) {
        const recoverableError = isInclusionRecoverableError(error);
        const explicitApiMessage =
          extractApiErrorMessage((error as any)?.response?.data) ||
          extractApiErrorMessage((error as any)?.errorMessage) ||
          extractApiErrorMessage((error as any)?.message);
        const isRateioValidationError =
          isRateioTotalValidationMessage(explicitApiMessage);
        const hasRecHint = /\brec\b|\brecno\b|r_e_c_n_o_/i.test(
          String(explicitApiMessage || "")
        );

        // Regra de negócio: quando o backend informa soma de rateio inválida,
        // não tenta reconciliação por timeout/500; exibe a mensagem imediatamente.
        if (isRateioValidationError && explicitApiMessage) {
          const customError = new Error(explicitApiMessage) as PostPreNotaHookError;
          customError.cause = "api_logic";
          (customError as any).errorMessage = explicitApiMessage;
          throw customError;
        }

        if (recoverableError || hasRecHint) {
          const recReconciliado = await reconcilePrenotaAfterTimeout({
            filial: draft.filial,
            numero: draft.documento.numero,
            serie: draft.documento.serie,
            fornecedor: draft.documento.fornecedor,
            loja: draft.documento.loja,
            origem: draft.origem,
          });

          if (recReconciliado) {
            resp = {
              Sucesso: true,
              REC: Number(recReconciliado),
            } as PostPreNotaResponse;
          } else {
            const reconcileError = new Error(
              "Timeout/erro 500 ao salvar. Após 30 segundos, não foi possível confirmar a criação da pré-nota."
            ) as PostPreNotaHookError;
            reconcileError.cause = "network";
            throw reconcileError;
          }
        }

        // Se o backend retornou mensagem explícita de negócio, prioriza exibir o erro
        // imediatamente quando não for um cenário de erro recuperável.
        if (!recoverableError && !hasRecHint && explicitApiMessage) {
          const customError = new Error(explicitApiMessage) as PostPreNotaHookError;
          customError.cause = "api_logic";
          (customError as any).errorMessage = explicitApiMessage;
          throw customError;
        }

        if (resp) {
          // Reconciliação obteve REC, segue fluxo normal.
        } else {
        // Novo tratamento: tenta pegar errorMessage mesmo sem response
        const errorMessage =
          extractApiErrorMessage((error as any)?.response?.data) ||
          (typeof (error as any)?.errorMessage === "string"
            ? (error as any).errorMessage
            : undefined);
        if (errorMessage) {
          // Lança um erro novo com a mensagem amigável para o onError capturar
          const customError = new Error(errorMessage) as PostPreNotaHookError;
          customError.cause = "api_logic";
          (customError as any).errorMessage = errorMessage;
          throw customError;
        }
        if (error instanceof Error) {
          throw error;
        }
        throw new Error("Erro desconhecido ao salvar pré-nota.");
        }
      }

      const isApiSuccess = (resp as any)?.Sucesso === true || (resp as any)?.Prenota === true;
      if (!isApiSuccess) {
        const fallbackMessage =
          extractApiErrorMessage(resp) ||
          "Erro ao salvar pré-nota. A API retornou falha sem detalhes.";
        const customError = new Error(fallbackMessage) as PostPreNotaHookError;
        customError.cause = "api_logic";
        (customError as any).errorMessage = fallbackMessage;
        throw customError;
      }

      // Fallback seguro para retorno
      safeReset();
      return resp ?? { Sucesso: false };
    },

    onMutate: () => {
      // Toast de loading com botão de cancelar
      const loadingToast = toast.custom(
        formatLoadingToast(() => {
          // Função de cancelamento
          toast.dismiss(loadingToast);
          toast.error("Operação cancelada pelo usuário", {
            duration: 3000,
          });
        }),
        {
          duration: Infinity,
        }
      );

      // Retorna o ID do toast para poder removê-lo depois
      return { loadingToast };
    },

    onError: (error: PostPreNotaHookError, variables, context) => {
      // Remove o toast de loading
      if (context?.loadingToast) {
        toast.dismiss(context.loadingToast);
      }

      // Log para depuração
      console.log("Erro na mutation:", error);

      // Erro de validação local
      if ((error as any).cause === "validation") {
        toast.error(error.message, {
          duration: 4000,
        });
        return;
      }

      // Erro de reconciliação pós-timeout
      if ((error as any).cause === "network") {
        toast.error(error.message || "Falha ao reconciliar pré-nota após timeout.", {
          duration: 7000,
        });
        return;
      }

      // Tenta extrair mensagem detalhada da API
      let errorMessage: string | undefined;
      // Caso seja AxiosError
      if ((error as any).response?.data) {
        let data = (error as any).response.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            // Se não for JSON válido, ignora
          }
        }
        if (data && typeof data === "object" && data.errorMessage) {
          errorMessage = data.errorMessage;
        }
      } else if ((error as any).errorMessage) {
        errorMessage = (error as any).errorMessage;
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      // Trata erros específicos do Protheus
      if (errorMessage) {
        if (errorMessage.includes("AJUDA:EXISTNF")) {
          // Toast customizado para nota já existente
          toast.custom(formatProtheusError(errorMessage), {
            duration: 8000,
          });
          return;
        } else {
          // Toast customizado para outros erros do Protheus
          toast.custom(formatProtheusError(errorMessage), {
            duration: 8000,
          });
          return;
        }
      }

      // Trata erros de lógica da API
      if ((error as any).cause === "api_logic") {
        const p = parse.protheus.error((error as any).message);
        if (p?.code === "AJUDAEXISTNF") {
          const mensagemAmigavel = `Nota fiscal ${p.nf} (série ${p.serie}), emitida em ${p.emissao}, já cadastrada para o fornecedor ${p.fornecedor}.`;
          toast.error(mensagemAmigavel, {
            duration: 6000,
          });
          return;
        }
      }

      // Toast genérico para erros não tratados
      toast.error("Erro ao salvar pré-nota. Tente novamente.", {
        duration: 4000,
      });
    },

    onSuccess: (data: PostPreNotaResponse, variables, context) => {
      // Remove o toast de loading
      if (context?.loadingToast) {
        toast.dismiss(context.loadingToast);
      }

      // Toast customizado com status detalhado
      toast.custom(formatProtheusSuccess(data), {
        duration: 8000,
      });

      // Invalida queries apenas se a pré-nota foi salva com sucesso
      if ((data as any).Prenota === true || data.Sucesso === true) {
        queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
      }
    },
  });
}
